<template>
  <!-- 静态预览图 -->
  <div class="_grid">
    <div
      v-for="item in previewItems"
      :key="item.basename"
      class="flex flex-col group"
    >
      <!-- 预览图 -->
      <div
        class="relative flex items-center justify-center border-box overflow-hidden w-50 h-50 mb-4 bg-[#404040] rounded-2xl border-[2px] border-solid border-[transparent] group-hover:border-[#0D84F2]"
      >
        <img
          v-if="previewSrcs[item.basename]"
          :src="previewSrcs[item.basename]?.base64"
          draggable="false"
          class="transition-all"
          :style="{
            height: '160px',
            width: size.width * scale + 'px'
            // borderRadius: radius * scale + 'px',
            // background: '#000000'
          }"
        />
        <div v-else class="text-red-500">预览图缺失</div>
        <!-- 刷新中loading -->
        <div
          v-if="regeneratingSet.has(item.basename)"
          class="absolute top-0 left-0 right-0 bottom-0 flex justify-center items-center bg-[#000]/[0.7]"
        >
          <a-spin size="large" tip="刷新中..." />
        </div>
        <div
          v-if="!regeneratingSet.has(item.basename)"
          class="absolute top-0 left-0 right-0 bottom-0 flex flex-col justify-center items-center gap-5 bg-[#000]/[0.7] opacity-0 group-hover:opacity-[1]"
        >
          <a-button class="" @click="() => uploadImage(item)"
            >上传图片</a-button
          >
          <a-button class="" type="primary" @click="() => regenerate(item)"
            >刷新</a-button
          >
        </div>
      </div>
      <div class="text-white w-52 overflow-hidden pt-1 px-2.5 pb-2">
        <div class="text-lg leading-none pb-2">
          {{ item.title }}
        </div>
        <div class="text-sm leading-none font-thin pb-2 opacity-[0.6] flex">
          <div class="flex-1">
            {{ `尺寸: ${item.width}*${item.height}` }}
          </div>
          <div>格式: png</div>
        </div>
        <div class="text-sm leading-none font-thin pb-2 opacity-[0.6]">
          {{
            `大小: <${filesize(filesizeLimit, {
              base: 2,
              standard: 'jedec'
            })}`
          }}
        </div>
        <div
          v-if="!validSize(item.basename) && previewSrcs[item.basename]"
          class="text-[#E66138] text-[13px]"
        >
          尺寸有误，请刷新或重新上传！
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { getWatchfaceDeviceRadius, useWatchfaceScreenShot } from '@/hooks';
import { WatchfaceService } from '@/services/watchface';
import { useImagesStore } from '@/store/use-images-store';
import { useLayerStore } from '@/store/use-layer-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import {
  getFileSize,
  getImageBase64,
  getImageSize,
  loadImageDom
} from '@/utils/fs/image.utils';
import { pickFile } from '@/utils/fs/pick-file.utils';
import { resizeImg, clipRoundImg } from '@/utils/transform-image';
import { Modal } from 'ant-design-vue';
import { filesize } from 'filesize';
import { computed, onMounted, ref, watch } from 'vue';

const layerStore = useLayerStore();
const { screenshot } = useWatchfaceScreenShot();
const imagesStore = useImagesStore();
const regeneratingSet = ref<Set<string>>(new Set());

interface PreviewImageItem {
  title: string;
  width: number;
  height: number;
  theme?: StoreThemeItem;
  basename: string;
  type: 'watchface' | 'theme';
}

const previewSrcs = ref<{
  [basename: string]: {
    base64: string;
    width: number;
    height: number;
  };
}>({});
const previewItems = computed(() => {
  const firstNormlTheme = layerStore.themes.find(
    (item) => item.type == 'normal'
  );
  const firstAodTheme = layerStore.themes.find(
    (item) => item.type == 'AOD' && item.name == firstNormlTheme?.name
  );
  const res: PreviewImageItem[] = [
    {
      basename: 'preview.png',
      title: '表盘预览图',
      // 预览图尺寸
      ...(watchfaceStore.previewEnhancedSize ?? watchfaceStore.previewSize),
      type: 'watchface',
      theme: firstNormlTheme
    },
    {
      basename: 'aod-preview.png',
      title: '息屏样式预览图',
      ...watchfaceStore.size,
      type: 'watchface',
      theme: firstAodTheme
    },
    {
      basename: 'market-preview.png',
      title: '表盘市场预览图',
      ...watchfaceStore.size,
      type: 'watchface',
      theme: firstNormlTheme
    }
  ];

  [
    ...layerStore.themes.filter((item) => item.type == 'normal'),
    ...layerStore.themes.filter((item) => item.type == 'AOD')
  ].forEach((theme, index) => {
    const title =
      theme.name + (theme.type == 'normal' ? '亮屏' : '息屏') + '预览图';
    res.push({
      basename: '_preview/' + title + '.png',
      title: title,
      type: 'theme',
      // N65A/B各个theme预览图尺寸保持一致，
      // 其他设备第一个预览图使用缩放，非第一个不使用缩放
      ...(index === 0 ||
      ['N65A', 'N65B', 'N65C'].includes(
        watchfaceStore.description.deviceType ?? ''
      )
        ? watchfaceStore.previewSize
        : watchfaceStore.size),
      theme
    });
  });

  // 过滤不存在theme的预览图
  return res.filter((item) => !!item.theme);
});

const watchfaceStore = useWatchfaceStore();

const size = computed(() => watchfaceStore.size);
const scale = computed(() => (size.value.height ? 160 / size.value.height : 1));

const radius = computed(() => {
  return getWatchfaceDeviceRadius(watchfaceStore.deviceType ?? '');
});
const watchfacePath = ref('');

const genearatePreviewSrcs = async (regenerateName?: string) => {
  const _watchfacePath = watchfacePath.value;
  if (!_watchfacePath) return;
  const service = new WatchfaceService(_watchfacePath);
  const previewService = service.preview;
  for (const previewItem of previewItems.value) {
    const { basename, type, theme, width, height } = previewItem;
    if (!theme) {
      previewSrcs.value[basename] = { base64: '', width: 0, height: 0 };
      continue;
    }

    const _regenerate = regenerateName == basename;
    if (previewSrcs.value[basename] && !_regenerate) continue;

    let baseUrl: string | undefined =
      type == 'watchface'
        ? await previewService.getPreviewImage(basename as 'preview.png')
        : imagesStore.image(basename)?.value;

    if (!baseUrl || _regenerate) {
      const isAOD = theme.type === 'AOD';
      let baseUrl2: string | undefined;

      if (isAOD) {
        // AOD直接单次渲染
        baseUrl2 = await screenshot(undefined, {
          themeName: theme.name,
          themeType: theme.type,
          usePreviewPointerData: true,
          useRivePreview: true,
        }) as string;
      } else {
        // normal主题：先用ignoreBg生成（避免黑边）
        baseUrl2 = await screenshot(undefined, {
          themeName: theme.name,
          themeType: theme.type,
          ignoreBg: true,
          usePreviewPointerData: true,
          useRivePreview: true,
        }) as string;

        // 自检：比较有背景版和无背景版的差异
        // 如果差异大（黑色背景表盘，ignoreBg后大面积内容丢失），回退到有背景版
        const bgVersion = await screenshot(undefined, {
          themeName: theme.name,
          themeType: theme.type,
          usePreviewPointerData: true,
          useRivePreview: true,
        }) as string;
        const bgImg = await loadImageDom(bgVersion);
        const noBgImg = await loadImageDom(baseUrl2);
        if (bgImg && noBgImg) {
          const cw = bgImg.width, ch = bgImg.height;
          const cc = document.createElement('canvas');
          cc.width = cw; cc.height = ch;
          const cctx = cc.getContext('2d') as CanvasRenderingContext2D;
          cctx.drawImage(bgImg, 0, 0);
          const bgData = cctx.getImageData(0, 0, cw, ch).data;
          cctx.clearRect(0, 0, cw, ch);
          cctx.drawImage(noBgImg, 0, 0);
          const noBgData = cctx.getImageData(0, 0, cw, ch).data;
          // 统计：有背景版不透明但无背景版透明的像素数（=纯背景色区域）
          let bgOnlyCount = 0, totalOpaque = 0;
          for (let i = 0; i < bgData.length; i += 4) {
            if (bgData[i + 3] === 255) {
              totalOpaque++;
              if (noBgData[i + 3] === 0) bgOnlyCount++;
            }
          }
          const bgOnlyRatio = totalOpaque > 0 ? bgOnlyCount / totalOpaque : 0;
          // 如果超过30%的不透明像素是纯背景色，说明是深色背景表盘，回退
          if (bgOnlyRatio > 0.3) {
            baseUrl2 = bgVersion;
          }
        }
      }

      // 圆角裁剪 + 缩放
      baseUrl2 = await clipRoundImg(baseUrl2 as string, radius.value);
      baseUrl2 = await resizeImg(baseUrl2 as string, { width, height });

      if (baseUrl2) {
        // 普通表盘预览图
        if (type == 'watchface') {
          await previewService.writeImage(basename, baseUrl2);
        } else {
          // 样式预览图
          const { width = 0, height = 0 } =
            (await getImageSize(undefined, baseUrl2)) ?? {};

          // 需要删除旧数据
          imagesStore.deleteImage(basename);
          await service.resource.deleteFile(basename);
          imagesStore.insertImage({
            basename,
            value: baseUrl2,
            width,
            height,
            realpath: ''
          });

          theme.preview = basename;
        }
        baseUrl = baseUrl2;
      }
    }
    if (baseUrl) {
      // 对所有来源的预览图统一做像素级圆角裁剪（包括磁盘缓存和新生成的）
      baseUrl = await clipRoundImg(baseUrl, radius.value * (width / (watchfaceStore.size.width || width))) || baseUrl;
      const { width: imgW = 0, height: imgH = 0 } =
        (await getImageSize(undefined, baseUrl)) ?? {};
      if (imgW && imgH) {
        previewSrcs.value[basename] = {
          base64: baseUrl,
          width: imgW,
          height: imgH
        };
      }
    }
  }
};

watch(previewItems, () => genearatePreviewSrcs(), { deep: true });

onMounted(() => {
  // initPreviewSrcs();
});

defineExpose({
  initData: async (_watchfacePath: string) => {
    if (!_watchfacePath) throw '_path必须提供';
    watchfacePath.value = _watchfacePath;
    previewSrcs.value = {};
    await genearatePreviewSrcs();
  },
  valid() {
    const emptyItem = previewItems.value?.find(
      (item) => !previewSrcs.value[item.basename]
    );
    const invalidSizeItem = previewItems.value?.find(
      (item) => !validSize(item.basename)
    );
    return {
      valid: emptyItem || invalidSizeItem ? false : true,
      err: emptyItem
        ? emptyItem?.title + '缺失，请检查！'
        : invalidSizeItem
        ? invalidSizeItem?.title + '尺寸错误，请修改；'
        : '预览图生成成功'
    };
  },
  regenerateAll: async () => {
    for (const item of previewItems.value) {
      await genearatePreviewSrcs(item.basename);
    }
  }
});

const filesizeLimit = 1024 * 200;

const regenerate = async (item: PreviewImageItem) => {
  regeneratingSet.value.add(item.basename);
  try {
    await genearatePreviewSrcs(item.basename);
  } finally {
    regeneratingSet.value.delete(item.basename);
  }
};

const uploadImage = async (item: PreviewImageItem) => {
  const _watchfacePath = watchfacePath.value;
  const { basename, width, height, type, theme } = item;
  if (!_watchfacePath || !theme) return;

  const previewService = new WatchfaceService(_watchfacePath).preview;
  const imagePath = await pickFile({
    extensions: ['png'],
    multi: false
  }).then((r) => r?.[0]);
  if (!imagePath) return;
  // 验证文件大小
  const fsize = (await getFileSize(imagePath)) as number;
  if (fsize > filesizeLimit) {
    Modal.warn({
      title: '图片错误',
      content: `图片体积不可超过${filesize(filesizeLimit, {
        base: 2,
        standard: 'jedec'
      })}`,
      zIndex: 9999
    });
    return;
  }

  // 验证图片尺寸
  const size = await getImageSize(imagePath);
  if (size?.width !== width || size.height !== height) {
    Modal.warn({
      title: '图片错误',
      content: `图片尺寸错误，图片大小必须为${width}*${height}px`,
      zIndex: 9999
    });
    return;
  }

  // 保存
  const baseUrl2 = await getImageBase64(imagePath);
  if (!baseUrl2) return;
  if (type == 'watchface') {
    await previewService.writeImage(basename, baseUrl2);
  } else {
    imagesStore.deleteImage(basename);
    await new WatchfaceService(_watchfacePath).resource.deleteFile(basename);
    imagesStore.insertImage({
      basename,
      value: baseUrl2,
      width,
      height,
      realpath: imagePath
    });
    theme.preview = basename;
  }
  previewSrcs.value[basename] = {
    base64: baseUrl2,
    width,
    height
  };
};

const validSize = (basename: string) => {
  const item = previewItems.value?.find((item) => item.basename === basename);
  const item2 = previewSrcs.value[basename] ?? {};
  if (!item2) return true;
  return item?.width === item2.width && item?.height === item2.height;
};
</script>
<style scoped lang="less">
._grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 200px);
  column-gap: 30px;
  row-gap: 15px;
  justify-content: center;
}
</style>
